import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ApiResponse } from '@card-software/library';
import { environment } from '../../environments/environment';
import {
  InternalTransfer,
  InternalTransferInsert,
  InternalTransferReceiveCommand,
  InternalTransferReturnCommand,
  InternalTransferSearchCommand,
  InternalTransferUpdate,
} from '../models/internal-transfer/internal-transfer';
import { BankAccount } from '../models/bank-account/bank-account';
import { CashRequest } from '../models/cash-request/cash-request';
import { PagedResult } from './bank-account.proxy.service';

@Injectable({ providedIn: 'root' })
export class InternalTransferProxyService {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = environment.proxyUrls.financeApi + '/api/v1/internal-transfers';

  public getById(id: string): Observable<ApiResponse<InternalTransfer>> {
    return this.httpClient.get<ApiResponse<InternalTransfer>>(`${this.baseUrl}/${id}`);
  }

  /**
   * Approved Cash Requests no live transfer has claimed yet. Pass the transfer being edited so its own
   * link stays in the list — a picker with no option matching the stored value renders blank, which reads
   * as "the save lost it".
   */
  public getLinkableCashRequests(transferId?: string | null): Observable<ApiResponse<CashRequest[]>> {
    const params = transferId ? new HttpParams().set('transferId', transferId) : undefined;
    return this.httpClient.get<ApiResponse<CashRequest[]>>(`${this.baseUrl}/linkable-cash-requests`, { params });
  }

  /**
   * Where this account can send money — the destination picker's options.
   *
   * <p>Asked of the API rather than filtered here, because which currencies a transfer may convert between
   * is a rule the server enforces and a client copy of it can only drift. A same-currency account is always
   * eligible; a different one only where the bank would actually be asked to convert.</p>
   */
  public getTransferableAccounts(fromAccountCode: string): Observable<ApiResponse<BankAccount[]>> {
    const params = new HttpParams().set('fromAccountCode', fromAccountCode);
    return this.httpClient.get<ApiResponse<BankAccount[]>>(`${this.baseUrl}/transferable-accounts`, { params });
  }

  public createDraft(data: InternalTransferInsert): Observable<ApiResponse<InternalTransfer>> {
    return this.httpClient.post<ApiResponse<InternalTransfer>>(this.baseUrl, data);
  }

  public updateDraft(id: string, data: InternalTransferUpdate): Observable<ApiResponse<InternalTransfer>> {
    return this.httpClient.put<ApiResponse<InternalTransfer>>(`${this.baseUrl}/${id}`, data);
  }

  /** Posts the send entry and the OUTCOME leg. No body — everything it needs is already on the leg. */
  public send(id: string): Observable<ApiResponse<InternalTransfer>> {
    return this.httpClient.post<ApiResponse<InternalTransfer>>(`${this.baseUrl}/${id}/send`, {});
  }

  /**
   * Starts recording what arrived: creates the INCOME leg as a draft, aimed at the destination account or,
   * for a return, back at the source. From there it is an ordinary bank movement — edit it through
   * `BankTransactionProxyService.updateDraft`, then call {@link receive} or {@link return} to post it.
   *
   * There is no leg until this is called. Nothing true is known about a receipt before someone is looking
   * at a statement, so conjuring one at send time would only put a fictional draft in the bank's list.
   */
  public createIncomingLeg(id: string, isReturn = false): Observable<ApiResponse<InternalTransfer>> {
    const params = new HttpParams().set('isReturn', isReturn);
    return this.httpClient.post<ApiResponse<InternalTransfer>>(`${this.baseUrl}/${id}/incoming-leg`, {}, { params });
  }

  /** Throws away an unposted incoming leg — the way back out of starting the wrong one. */
  public discardIncomingLeg(id: string): Observable<ApiResponse<InternalTransfer>> {
    return this.httpClient.delete<ApiResponse<InternalTransfer>>(`${this.baseUrl}/${id}/incoming-leg`);
  }

  /**
   * Posts the receive entry and the INCOME leg. Everything it needs is already on the leg, except the one
   * thing the leg cannot derive: on a transfer the bank converted, the rate it gave.
   */
  public receive(id: string, command: InternalTransferReceiveCommand = {}): Observable<ApiResponse<InternalTransfer>> {
    return this.httpClient.post<ApiResponse<InternalTransfer>>(`${this.baseUrl}/${id}/receive`, command);
  }

  /** The wire bounced: books the money back into the SOURCE account. Final — there is no undo. */
  public return(id: string, command: InternalTransferReturnCommand): Observable<ApiResponse<InternalTransfer>> {
    return this.httpClient.post<ApiResponse<InternalTransfer>>(`${this.baseUrl}/${id}/return`, command);
  }

  public cancel(id: string): Observable<ApiResponse<InternalTransfer>> {
    return this.httpClient.post<ApiResponse<InternalTransfer>>(`${this.baseUrl}/${id}/cancel`, {});
  }

  public uploadAttachment(id: string, file: File): Observable<ApiResponse<InternalTransfer>> {
    const body = new FormData();
    body.append('file', file, file.name);
    return this.httpClient.post<ApiResponse<InternalTransfer>>(`${this.baseUrl}/${id}/attachment`, body);
  }

  public search(
    command: InternalTransferSearchCommand,
    pageNumber = 1,
    pageSize = 25,
    matchAny = false,
    sortField?: string,
    sortOrder = -1,
  ): Observable<ApiResponse<PagedResult<InternalTransfer>>> {
    let params = new HttpParams()
      .set('pageNumber', pageNumber)
      .set('pageSize', pageSize)
      .set('matchAny', matchAny)
      .set('sortOrder', sortOrder);
    if (sortField) params = params.set('sortField', sortField);
    return this.httpClient.post<ApiResponse<PagedResult<InternalTransfer>>>(`${this.baseUrl}/search`, command, { params });
  }
}
